/**
 * Shared form state and handlers for real wedding and blog admin forms.
 */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { useRealWeddingReducer } from "../../Utility";
import {
  onRealWeddingPost,
  onRealWeddingPostReset,
  onGetRealWedding,
  onGetRealWeddingReset,
} from "../../Store/Slices/realWeddingSlice";

export const initialPhoto = () => ({ file: null, image_url: "", display_order: 0 });

export function revokeBlobUrl(url) {
  if (typeof url === "string" && url.startsWith("blob:")) {
    try {
      URL.revokeObjectURL(url);
    } catch (_) {}
  }
}

const emptyForm = (contentType) => ({
  content_type: contentType,
  heading: "",
  city: "",
  cover_photo: "",
  description: "",
  wedding_photos: [initialPhoto()],
  category: "",
  author: "WeddingzVenue Team",
  read_time_minutes: 5,
  metatitle: "",
  metadescp: "",
  slug: "",
});

export function useContentForm(contentType) {
  const isBlog = contentType === "blog";
  const { id: editId } = useParams();
  const isEditMode = Boolean(editId);
  const dispatch = useDispatch();
  const {
    postLoading,
    postData,
    postError,
    postMessage,
    getOneData,
    getOneLoading,
    getOneError,
  } = useRealWeddingReducer();

  const [form, setForm] = useState(emptyForm(contentType));
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [coverFile, setCoverFile] = useState(null);
  const [loadTypeMismatch, setLoadTypeMismatch] = useState(false);
  const submitInProgressRef = useRef(false);
  const coverInputRef = useRef(null);
  const galleryInputRefs = useRef({});
  const multiFileInputRef = useRef(null);

  useEffect(() => {
    if (!submitInProgressRef.current || postLoading) return;
    submitInProgressRef.current = false;
    if (postError) {
      toast.error(postMessage || (isBlog ? "Failed to save blog" : "Failed to save real wedding"));
      return;
    }
    if (postData) {
      setSuccess(true);
      revokeBlobUrl(form.cover_photo);
      form.wedding_photos.forEach((p) => revokeBlobUrl(p.image_url));
      setForm(emptyForm(contentType));
      setCoverFile(null);
      setErrors({});
      if (coverInputRef.current) coverInputRef.current.value = "";
      dispatch(onRealWeddingPostReset());
    }
  }, [postLoading, postData, postError, postMessage]);

  useEffect(() => {
    if (editId) {
      dispatch(onRealWeddingPostReset());
      dispatch(onGetRealWedding(editId));
    }
    return () => {
      if (editId) dispatch(onGetRealWeddingReset());
    };
  }, [editId, dispatch]);

  useEffect(() => {
    if (!isEditMode || !getOneData) return;
    const w = getOneData;
    const loadedType = w.content_type === "blog" ? "blog" : "real_wedding";
    if (loadedType !== contentType) {
      setLoadTypeMismatch(true);
      return;
    }
    setLoadTypeMismatch(false);

    const metaTitle = w?.metatitle ?? w?.meta_title ?? w?.metaTitle ?? "";
    const metaDesc =
      w?.metadescp ?? w?.meta_description ?? w?.meta_desc ?? w?.metaDescription ?? "";
    const slugValue = w?.slug ?? w?.seo_slug ?? w?.url_slug ?? "";
    const coverUrl =
      w.cover_photo ||
      (Array.isArray(w.wedding_photos)
        ? w.wedding_photos.find((p) => p?.image_type === "cover")?.image_url ||
          w.wedding_photos[0]?.image_url
        : null) ||
      "";
    const gallery =
      Array.isArray(w.wedding_photos) && w.wedding_photos.length > 0
        ? w.wedding_photos
            .filter((p) => (p?.image_type || "gallery") === "gallery")
            .map((p) => ({
              file: null,
              image_url: p?.image_url ?? "",
              display_order: p?.display_order ?? 0,
            }))
        : [initialPhoto()];

    setForm({
      content_type: contentType,
      heading: w.heading ?? "",
      city: w.city ?? w.venue_city ?? "",
      cover_photo: coverUrl,
      description: w.description ?? "",
      wedding_photos: gallery.length ? gallery : [initialPhoto()],
      category: w.category ?? "",
      author: w.author ?? "WeddingzVenue Team",
      read_time_minutes: w.read_time_minutes ?? 5,
      metatitle: metaTitle,
      metadescp: metaDesc,
      slug: slugValue,
    });
    setCoverFile(null);
  }, [isEditMode, getOneData, contentType]);

  const setField = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: "" }));
  };

  const setPhoto = (index, updates) => {
    setForm((p) => ({
      ...p,
      wedding_photos: p.wedding_photos.map((photo, i) =>
        i === index ? { ...photo, ...updates } : photo
      ),
    }));
  };

  const handleCoverChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    revokeBlobUrl(form.cover_photo);
    setCoverFile(file);
    setField("cover_photo", URL.createObjectURL(file));
  };

  const handleGalleryChange = (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((p) => {
      const photo = p.wedding_photos[index];
      revokeBlobUrl(photo?.image_url);
      return {
        ...p,
        wedding_photos: p.wedding_photos.map((ph, i) =>
          i === index ? { ...ph, file, image_url: URL.createObjectURL(file) } : ph
        ),
      };
    });
  };

  const handleMultiSelect = (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    let nextOrder =
      form.wedding_photos.length === 0
        ? 0
        : Math.max(...form.wedding_photos.map((p) => p.display_order), -1) + 1;
    const newPhotos = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file?.type?.startsWith("image/")) continue;
      newPhotos.push({
        file,
        image_url: URL.createObjectURL(file),
        display_order: nextOrder++,
      });
    }
    setForm((p) => ({
      ...p,
      wedding_photos: [...p.wedding_photos, ...newPhotos],
    }));
    e.target.value = "";
  };

  const removeCover = () => {
    revokeBlobUrl(form.cover_photo);
    setField("cover_photo", "");
    setCoverFile(null);
    if (coverInputRef.current) coverInputRef.current.value = "";
  };

  const addPhoto = () => {
    const nextOrder =
      form.wedding_photos.length === 0
        ? 0
        : Math.max(...form.wedding_photos.map((p) => p.display_order), -1) + 1;
    setForm((p) => ({
      ...p,
      wedding_photos: [...p.wedding_photos, { ...initialPhoto(), display_order: nextOrder }],
    }));
  };

  const removePhoto = (index) => {
    if (form.wedding_photos.length <= 1) return;
    const photo = form.wedding_photos[index];
    revokeBlobUrl(photo?.image_url);
    setForm((p) => ({
      ...p,
      wedding_photos: p.wedding_photos.filter((_, i) => i !== index),
    }));
  };

  const resetForAnother = () => {
    setSuccess(false);
    setForm(emptyForm(contentType));
    setErrors({});
    setCoverFile(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const e2 = {};
    if (!form.heading?.trim()) e2.heading = "Heading is required";
    if (!form.city?.trim()) e2.city = "City is required";
    if (!isEditMode && !coverFile) e2.cover_photo = "Cover photo is required";
    if (isEditMode && !coverFile && !form.cover_photo?.trim()) {
      e2.cover_photo = "Cover photo is required";
    }
    if (!form.description?.trim()) e2.description = "Description is required";
    else if (!form.description.replace(/<[^>]*>/g, "").trim()) {
      e2.description = "Description is required";
    }
    if (isBlog && !form.category?.trim()) e2.category = "Category is required";
    const invalidPhotos = form.wedding_photos.some((p) => !p.file && !p.image_url);
    if (invalidPhotos) e2.wedding_photos = "All photo slots must have an image.";
    setErrors(e2);
    if (Object.keys(e2).length > 0) return;

    const wedding_photos = [
      { image: coverFile || form.cover_photo || "", type: "cover" },
      ...form.wedding_photos
        .filter((p) => p.file || p.image_url)
        .map((p) => ({
          image: p.file || p.image_url || "",
          type: "gallery",
          display_order: p.display_order ?? 0,
        })),
    ];
    submitInProgressRef.current = true;
    const payload = {
      content_type: contentType,
      heading: form.heading.trim(),
      city: form.city.trim(),
      description: form.description.trim(),
      wedding_photos,
    };
    if (isBlog) {
      payload.category = form.category.trim();
      payload.author = form.author?.trim() || "WeddingzVenue Team";
      payload.read_time_minutes = Math.max(0, parseInt(form.read_time_minutes, 10) || 5);
      payload.metatitle = form.metatitle?.trim() ?? "";
      payload.metadescp = form.metadescp?.trim() ?? "";
      payload.slug = form.slug?.trim() ?? "";
    }
    if (isEditMode && editId) payload.id = editId;
    dispatch(onRealWeddingPost(payload));
  };

  return {
    isBlog,
    isEditMode,
    editId,
    form,
    errors,
    success,
    coverFile,
    postLoading,
    getOneLoading,
    getOneError,
    loadTypeMismatch,
    coverInputRef,
    galleryInputRefs,
    multiFileInputRef,
    setField,
    setPhoto,
    handleCoverChange,
    handleGalleryChange,
    handleMultiSelect,
    removeCover,
    addPhoto,
    removePhoto,
    resetForAnother,
    handleSubmit,
  };
}
